import { type ReactNode } from "react";
import { useQueries } from "@tanstack/react-query";
import { pnaAvailabilityQueryOptions } from "~/domains/pna/pnaQueries";
import { toAvailabilityOutcomeFromQuery } from "~/domains/pna/pnaSnapshot";
import { type PreferredProductData } from "~/features/productDetails/models/PdpPageProductData";
import { selectProductIsInStock } from "~/utils/pna/availabilitySelectors";

interface StockDataProviderProps {
  children: (props: StockDataProviderState) => ReactNode;
  products: PreferredProductData[];
}

export interface StockDataProviderState {
  inStock: { [code: string]: boolean };
  isLoading: boolean;
  isError: boolean;
}

const StockDataProvider = ({ children, products }: StockDataProviderProps) => {
  // Preferred products are never staged into `products.basic` (the PDP stages only the sale
  // cards); the code-based query transports each card's raw code directly.
  const availabilityOptions = products.map((product) => pnaAvailabilityQueryOptions({ productCode: product.code }));
  const availabilityQueries = useQueries({ queries: availabilityOptions });

  const outcomes = availabilityQueries.map((query, index) =>
    toAvailabilityOutcomeFromQuery(query, availabilityOptions[index].enabled === true),
  );

  const inStock = outcomes.reduce<{ [code: string]: boolean }>((acc, outcome) => {
    if (outcome.status === "success" && outcome.data) {
      acc[outcome.data.code] = selectProductIsInStock(outcome.data);
    }
    return acc;
  }, {});

  return (
    <>
      {children({
        inStock,
        isLoading: outcomes.some((outcome) => outcome.status === "pending"),
        isError: outcomes.some((outcome) => outcome.status === "error"),
      })}
    </>
  );
};

export default StockDataProvider;
